/** Edit and retire a vendor, in a side drawer - ADR-0212 decision 4 (rename and retire).
 *
 * **A vendor is renamed and retired, never deleted.** Renaming corrects how the name is written;
 * the server refuses a name another vendor holds, so two parties are never merged by a spelling.
 * Retiring keeps every account, meter link and figure that names the vendor, so a past report does
 * not change; it only stops a *new* account being recorded and the vendor being suggested for a bill.
 * Restoring lifts that. The drawer is offered to an Admin or an Approver only, as Add is (the server
 * answers 403 to anyone else, and a control that predictably fails is not drawn: design rule 6).
 */

import { useState } from "react";

import { useRenameVendor, useRestoreVendor, useRetireVendor } from "../../api/sources";
import type { RegisterVendor } from "../../api/sources";
import { WriteFailed } from "../../components/Loaded";
import { Drawer } from "../../components/ui/Drawer";
import { InfoPopover } from "../../components/ui/InfoPopover";
import { StatusChip } from "../../components/ui/StatusChip";
import styles from "./Vendors.module.css";

export function EditVendorDrawer({
  vendor,
  onClose,
}: {
  vendor: RegisterVendor | undefined;
  onClose: () => void;
}) {
  // Keyed by the vendor, so opening another starts from its own name rather than the last typed one.
  return (
    <Drawer open={vendor !== undefined} onClose={onClose} title="Edit vendor" width="sm">
      {vendor ? <EditForm key={vendor.id} vendor={vendor} onClose={onClose} /> : null}
    </Drawer>
  );
}

function EditForm({ vendor, onClose }: { vendor: RegisterVendor; onClose: () => void }) {
  const rename = useRenameVendor();
  const retire = useRetireVendor();
  const restore = useRestoreVendor();
  const [name, setName] = useState(vendor.name);
  const [refusal, setRefusal] = useState<unknown>(null);
  const busy = rename.isPending || retire.isPending || restore.isPending;
  const unchanged = name.trim() === vendor.name;

  return (
    <div className={styles.form} data-testid="edit-vendor">
      <form
        id="rename-vendor"
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          setRefusal(null);
          rename.mutateAsync({ id: vendor.id, name: name.trim() }).then(onClose, (error: unknown) => setRefusal(error));
        }}
      >
        <label className={styles.field}>
          <span>Name as it prints on a bill</span>
          <input
            value={name}
            required
            maxLength={200}
            autoComplete="off"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <button
          type="submit"
          className={styles.primary}
          disabled={!name.trim() || unchanged || busy}
          data-testid="rename-vendor"
        >
          {rename.isPending ? "Saving…" : "Save name"}
        </button>
      </form>
      <div className={styles.form}>
        {vendor.is_retired ? (
          <>
            <StatusChip tone="neutral" size="sm" testId="vendor-retired-chip">
              Retired
            </StatusChip>
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              data-testid="restore-vendor"
              onClick={() => {
                setRefusal(null);
                restore.mutateAsync({ id: vendor.id }).then(onClose, (error: unknown) => setRefusal(error));
              }}
            >
              {restore.isPending ? "Restoring…" : "Restore vendor"}
            </button>
          </>
        ) : (
          <button
            type="button"
            className={styles.ghost}
            disabled={busy}
            data-testid="retire-vendor"
            onClick={() => {
              setRefusal(null);
              retire.mutateAsync({ id: vendor.id }).then(onClose, (error: unknown) => setRefusal(error));
            }}
          >
            {retire.isPending ? "Retiring…" : "Retire vendor"}
          </button>
        )}
        <InfoPopover infoKey="vendors.retire-vendor" />
      </div>
      <WriteFailed error={refusal} of="the vendor register" onReload={() => setRefusal(null)} />
    </div>
  );
}
